/**
 * Metric-specific ranking eligibility for sector-based municipal inventories.
 *
 * ClimatePlans city total = LDV + HDV + residential electricity + commercial electricity
 * + residential NG + commercial NG + waste + off-road (+ agriculture when present).
 * Agriculture is optional for municipal inventories (often 0 or not applicable).
 * Water/wastewater is not modeled and is never required for ranking.
 * CBEI, utility identity, and demographic completeness are not ranking gates.
 */

import type { RankingMetric, RankingPoolMember } from './types';

export const RANKING_UNIVERSE_VERSION_ID = 'CA_MUNICIPAL_FROZEN_2023_INVENTORY_v1';

export const CORE_TOTAL_SECTORS = [
  'ldv',
  'hdv',
  'elec_res',
  'elec_com',
  'ng_res',
  'ng_com',
  'waste',
  'offroad',
] as const;

export type CoreTotalSector = typeof CORE_TOTAL_SECTORS[number];

export const OPTIONAL_MUNICIPAL_SECTORS = ['agriculture'] as const;

export type RankingExclusionReason =
  | 'NOT_IN_FROZEN_UNIVERSE'
  | 'MISSING_CANONICAL_GEOGRAPHY'
  | 'YEAR_MISMATCH'
  | 'MISSING_TOTAL'
  | 'MISSING_CORE_SECTOR'
  | 'PARTIAL_TOTAL_SCOPE'
  | 'MISSING_OR_NONPOSITIVE_POPULATION';

export interface RankingEligibilityAssessment {
  eligible: boolean;
  exclusionReasons: RankingExclusionReason[];
  missingCoreSectors: string[];
  sectorCoverageUsed: string[];
  rankingMetric: RankingMetric;
  inventoryYear: number;
  universeVersionId: string;
  comparableBoundary: boolean;
}

export interface RankingCohortSummary {
  rankingMetric: RankingMetric;
  inventoryYear: number;
  universeVersionId: string;
  universeSize: number;
  qualifiedCount: number;
  excludedCount: number;
  sectorCoverageUsed: string[];
}

export function presentSectorValue(value: number | null | undefined): boolean {
  return value != null && Number.isFinite(value);
}

/**
 * Electricity coverage requires both an emissions value and kWh activity.
 * Stored 0.0 emissions with null kWh (Alameda 2023) is a fill, not an inventoried zero.
 */
export function electricitySectorCovered(
  emissions: number | null | undefined,
  kwh: number | null | undefined
): boolean {
  return presentSectorValue(emissions) && presentSectorValue(kwh);
}

export function coreSectorCovered(member: RankingPoolMember, code: CoreTotalSector): boolean {
  if (code === 'elec_res') {
    return electricitySectorCovered(member.coreSectors.elec_res, member.coreActivity?.elec_res_kwh);
  }
  if (code === 'elec_com') {
    return electricitySectorCovered(member.coreSectors.elec_com, member.coreActivity?.elec_com_kwh);
  }
  return presentSectorValue(member.coreSectors[code]);
}

export function assessSectorTotalRankingEligibility(
  member: RankingPoolMember,
  inventoryYear: number,
  universeVersionId = RANKING_UNIVERSE_VERSION_ID
): RankingEligibilityAssessment {
  const reasons: RankingExclusionReason[] = [];
  const missingCore: string[] = [];

  if (!member.inFrozenUniverse) reasons.push('NOT_IN_FROZEN_UNIVERSE');
  if (member.geofips == null) reasons.push('MISSING_CANONICAL_GEOGRAPHY');
  if (member.inventoryYear == null || member.inventoryYear !== inventoryYear) {
    reasons.push('YEAR_MISMATCH');
  }
  if (!presentSectorValue(member.totalMt)) reasons.push('MISSING_TOTAL');

  for (const code of CORE_TOTAL_SECTORS) {
    if (!coreSectorCovered(member, code)) missingCore.push(code);
  }
  if (missingCore.length > 0) {
    reasons.push('MISSING_CORE_SECTOR');
    reasons.push('PARTIAL_TOTAL_SCOPE');
  }

  const comparableBoundary = member.inFrozenUniverse && member.geofips != null;
  return {
    eligible: reasons.length === 0,
    exclusionReasons: Array.from(new Set(reasons)),
    missingCoreSectors: missingCore,
    sectorCoverageUsed: [...CORE_TOTAL_SECTORS],
    rankingMetric: 'state_total',
    inventoryYear,
    universeVersionId,
    comparableBoundary,
  };
}

export function assessPerCapitaRankingEligibility(
  member: RankingPoolMember,
  inventoryYear: number,
  universeVersionId = RANKING_UNIVERSE_VERSION_ID
): RankingEligibilityAssessment {
  const base = assessSectorTotalRankingEligibility(member, inventoryYear, universeVersionId);
  const reasons = [...base.exclusionReasons];
  if (member.population == null || !Number.isFinite(member.population) || member.population <= 0) {
    reasons.push('MISSING_OR_NONPOSITIVE_POPULATION');
  }
  return {
    ...base,
    eligible: reasons.length === 0,
    exclusionReasons: Array.from(new Set(reasons)),
    rankingMetric: 'state_per_capita',
  };
}

export function assessRankingEligibility(
  member: RankingPoolMember,
  metric: RankingMetric,
  inventoryYear: number
): RankingEligibilityAssessment {
  const isPerCapita = metric === 'state_per_capita' || metric === 'county_per_capita';
  const assessment = isPerCapita
    ? assessPerCapitaRankingEligibility(member, inventoryYear)
    : assessSectorTotalRankingEligibility(member, inventoryYear);
  return { ...assessment, rankingMetric: metric };
}

export function qualifiedRankingMembers(
  pool: RankingPoolMember[],
  metric: RankingMetric,
  inventoryYear: number
): RankingPoolMember[] {
  return pool.filter((m) => assessRankingEligibility(m, metric, inventoryYear).eligible);
}

export function summarizeRankingCohort(
  pool: RankingPoolMember[],
  metric: RankingMetric,
  inventoryYear: number,
  universeSize: number,
  universeVersionId = RANKING_UNIVERSE_VERSION_ID
): RankingCohortSummary {
  const qualified = qualifiedRankingMembers(pool, metric, inventoryYear);
  return {
    rankingMetric: metric,
    inventoryYear,
    universeVersionId,
    universeSize,
    qualifiedCount: qualified.length,
    excludedCount: universeSize - qualified.length,
    sectorCoverageUsed: [...CORE_TOTAL_SECTORS],
  };
}

export function emptyCoreSectors(): RankingPoolMember['coreSectors'] {
  return {
    ldv: null,
    hdv: null,
    elec_res: null,
    elec_com: null,
    ng_res: null,
    ng_com: null,
    waste: null,
    offroad: null,
  };
}

export function emptyCoreActivity(): RankingPoolMember['coreActivity'] {
  return { elec_res_kwh: null, elec_com_kwh: null };
}
